import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type EventSeverity = "info" | "warning" | "error";
export type EventSource = "user" | "system" | "sync";

export interface EventLogEntry {
  id: string;
  event_type: string;
  source: EventSource;
  entity_type: string | null;
  entity_id: string | null;
  project_id: string | null;
  user_id: string | null;
  payload: Record<string, unknown> | null;
  severity: EventSeverity;
  created_at: string;
  // Joined
  users?: { full_name: string } | null;
  project?: { project_code: string | null; name: string | null } | null;
}

export interface EventLogFilters {
  source?: EventSource;
  severity?: EventSeverity;
  entity_type?: string;
  project_id?: string;
  user_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useEventLog(filters?: EventLogFilters) {
  const limit = filters?.limit ?? 50;
  const offset = filters?.offset ?? 0;

  return useQuery({
    queryKey: [
      "event-log",
      filters?.source,
      filters?.severity,
      filters?.entity_type,
      filters?.project_id,
      filters?.user_id,
      filters?.search,
      limit,
      offset,
    ],
    queryFn: async () => {
      let query = supabase
        .from("event_log")
        .select(
          "*, users:user_id(full_name), project:project_id(project_code, name)",
          { count: "exact" },
        )
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (filters?.source) query = query.eq("source", filters.source);
      if (filters?.severity) query = query.eq("severity", filters.severity);
      if (filters?.entity_type) query = query.eq("entity_type", filters.entity_type);
      if (filters?.project_id) query = query.eq("project_id", filters.project_id);
      if (filters?.user_id) query = query.eq("user_id", filters.user_id);
      if (filters?.search) {
        query = query.ilike("event_type", `%${filters.search}%`);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return {
        rows: (data ?? []) as EventLogEntry[],
        total: count ?? 0,
      };
    },
  });
}

/**
 * Log an event from the frontend.
 * Silent on failure so logging-bugs aldri kan velte en mutation.
 */
export async function logEvent(input: {
  event_type: string;
  source?: EventSource;
  entity_type?: string;
  entity_id?: string;
  project_id?: string;
  user_id?: string;
  payload?: Record<string, unknown>;
  severity?: EventSeverity;
}) {
  const { error } = await supabase.from("event_log").insert({
    event_type: input.event_type,
    source: input.source ?? "user",
    entity_type: input.entity_type ?? null,
    entity_id: input.entity_id ?? null,
    project_id: input.project_id ?? null,
    user_id: input.user_id ?? null,
    payload: input.payload ?? null,
    severity: input.severity ?? "info",
  });
  if (error) console.error("Failed to log event:", error);
}
